// ---------------------------------------------------------------------------
// One correlation ID per user action, propagated client -> server -> provider
// -> response, plus a safe step timeline so an admin can tell an empty
// response, a timeout, a server crash, a deployment mismatch and a
// serialization failure apart.
//
// GUARANTEES
//  - never throws into the caller;
//  - never records bodies, OTPs, tokens, headers or keys;
//  - writes are batched and fire-and-forget (never on a wallet/bet path).
// ---------------------------------------------------------------------------

import { supabase } from "@/integrations/supabase/client";
import { redactObject, redactString } from "./redact";
import { noteFlush, noteQueued } from "./health";

export const CORRELATION_HEADER = "x-correlation-id";

/** Canonical safe timeline steps (Part 2 of the verified fix workflow). */
export const TIMELINE_STEPS = [
  "client_action_started",
  "request_sent",
  "server_function_entered",
  "provider_stage",
  "response_created",
  "response_received",
  "response_parsed",
  "session_completed",
] as const;

export type TimelineStep = (typeof TIMELINE_STEPS)[number] | (string & {});
export type TimelineStatus = "ok" | "fail" | "pending" | "skipped";

export type TimelineInput = {
  correlation_id: string;
  step: TimelineStep;
  status?: TimelineStatus;
  source?: "client" | "server";
  error_id?: string | null;
  code?: string | null;
  duration_ms?: number | null;
  detail?: string | null;
  metadata?: Record<string, unknown>;
};

function rand(len = 8): string {
  const A = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  const g = typeof globalThis.crypto?.getRandomValues === "function" ? globalThis.crypto : null;
  if (g) {
    const buf = new Uint32Array(len);
    g.getRandomValues(buf);
    for (let i = 0; i < len; i++) out += A[buf[i] % A.length];
  } else {
    for (let i = 0; i < len; i++) out += A[Math.floor(Math.random() * A.length)];
  }
  return out;
}

export function newCorrelationId(): string {
  return `cor_${Date.now().toString(36)}_${rand(8)}`;
}

// --- current action context -------------------------------------------------

let current: { id: string; seq: number; startedAt: number } | null = null;

export function currentCorrelationId(): string | null {
  return current?.id ?? null;
}

/** Starts a correlated action. Returns the id; always call endCorrelation(). */
export function startCorrelation(action: string, metadata?: Record<string, unknown>): string {
  const id = newCorrelationId();
  current = { id, seq: 0, startedAt: Date.now() };
  recordStep({ correlation_id: id, step: "client_action_started", detail: action, metadata });
  return id;
}

export function endCorrelation(
  status: TimelineStatus = "ok",
  detail?: string,
  metadata?: Record<string, unknown>,
): void {
  if (!current) return;
  const { id, startedAt } = current;
  recordStep({
    correlation_id: id,
    step: "session_completed",
    status,
    detail,
    duration_ms: Date.now() - startedAt,
    metadata,
  });
  current = null;
}

/** Runs fn inside a correlation scope. Never swallows the caller's error. */
export async function withCorrelation<T>(
  action: string,
  fn: (correlationId: string) => Promise<T>,
  metadata?: Record<string, unknown>,
): Promise<T> {
  const id = startCorrelation(action, metadata);
  try {
    const out = await fn(id);
    endCorrelation("ok");
    return out;
  } catch (err) {
    endCorrelation("fail", redactString(String((err as any)?.message ?? err)).slice(0, 300));
    throw err;
  }
}

// --- batched writer ---------------------------------------------------------

const FLUSH_MS = 3000;
const MAX_QUEUE = 300;
let queue: Record<string, unknown>[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule() {
  if (timer || typeof window === "undefined") return;
  timer = setTimeout(() => {
    timer = null;
    void flushTimeline();
  }, FLUSH_MS);
}

export async function flushTimeline(): Promise<void> {
  const batch = queue.splice(0, 50);
  if (!batch.length) return;
  try {
    let uid: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      uid = data.session?.user?.id ?? null;
    } catch {
      uid = null;
    }
    await supabase
      .from("diagnostics_timeline")
      .insert(batch.map((b) => ({ ...b, user_id: uid })) as any);
    noteFlush(true, queue.length);
  } catch {
    // Diagnostics must never surface or retry-storm.
    noteFlush(false, queue.length);
  }
  if (queue.length) schedule();
}

/** Records one safe timeline step. Synchronous, non-blocking, never throws. */
export function recordStep(input: TimelineInput): void {
  try {
    if (!input.correlation_id) return;
    const seq = current && current.id === input.correlation_id ? ++current.seq : Date.now() % 100000;
    if (queue.length < MAX_QUEUE) {
      queue.push({
        correlation_id: input.correlation_id,
        seq,
        step: input.step,
        source: input.source ?? "client",
        status: input.status ?? "ok",
        error_id: input.error_id ?? null,
        code: input.code ?? null,
        duration_ms:
          typeof input.duration_ms === "number" ? Math.round(input.duration_ms) : null,
        detail: input.detail ? redactString(String(input.detail)).slice(0, 500) : null,
        metadata: redactObject(input.metadata ?? {}),
        created_at: new Date().toISOString(),
      });
      noteQueued("timeline", queue.length);
    }
    if (input.status === "fail") void flushTimeline();
    else schedule();
  } catch {
    /* never break the caller */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => void flushTimeline());
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushTimeline();
  });
}
